import Cookies from 'js-cookie';

import { isClient } from '../../../utilities';

import { isLocalDevHost } from './localDevHost';

const DEV_URL = 'https://tst.avios.com';
const LOCALE_SEGMENT_PATTERN = /^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i;
const getCurrentOrigin = () => (isClient() ? window.location.origin : '');

const stripLocaleFromReturnTo = (returnTo?: string | false) => {
  if (!returnTo) {
    return returnTo;
  }

  try {
    const normalizedUrl = new URL(returnTo);
    const pathnameWithoutLocale = normalizedUrl.pathname.replace(LOCALE_SEGMENT_PATTERN, '') || '/';

    normalizedUrl.pathname = pathnameWithoutLocale;

    return normalizedUrl.toString();
  } catch {
    return returnTo;
  }
};

export const AuthService = {
  login: async ({
    silent,
    opco,
    iframe,
    overrideReturnTo,
    localDevHostPatterns,
    gatewayUrl,
  }: {
    silent?: boolean;
    opco?: string;
    iframe?: boolean;
    overrideReturnTo?: string;
    localDevHostPatterns?: string[];
    gatewayUrl?: string;
  } = {}) => {
    const returnTo =
      stripLocaleFromReturnTo(isClient() && (overrideReturnTo ? `${window.location.origin}${overrideReturnTo}` : window.location.href)) ?? '';
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());

    const url = `${baseUrl}/auth-gateway/login?onsuccess=${encodeURIComponent(returnTo)}${opco ? `&opco=${opco}` : ''}${iframe ? '&iframe=true' : ''}${silent ? '&prompt=none' : ''}${isLocalhost ? '&env=local' : ''}`;

    return fetch(url, {
      method: 'GET',
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },

  logout: async ({
    overrideReturnTo,
    localDevHostPatterns,
    gatewayUrl,
  }: { overrideReturnTo?: string; localDevHostPatterns?: string[]; gatewayUrl?: string } = {}) => {
    const returnTo =
      stripLocaleFromReturnTo(isClient() && (overrideReturnTo ? `${window.location.origin}${overrideReturnTo}` : window.location.href)) ?? '';
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());

    const opco = Cookies.get('AVIOS_OPCO')?.toLowerCase();
    const sessionParamName = `${opco}_session_id`;
    const deleteParamName = `delete_${sessionParamName}`;

    const sessionId = Cookies.get(sessionParamName);

    const url = `${baseUrl}/auth-gateway/logout?returnto=${encodeURIComponent(`${returnTo}${isLocalhost ? `?${deleteParamName}=true` : ''}`)}${isLocalhost && sessionId ? `&${sessionParamName}=${sessionId}` : ''}${isLocalhost ? '&env=local' : ''}`;

    return fetch(`${url}`, {
      method: 'GET',
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },

  createLoginUrl: ({
    silent,
    overrideReturnTo,
    opco,
    iframe,
    localDevHostPatterns,
    gatewayUrl,
  }: {
    silent?: boolean;
    overrideReturnTo?: string;
    opco?: string;
    iframe?: boolean;
    localDevHostPatterns?: string[];
    gatewayUrl?: string;
  } = {}) => {
    const returnTo =
      stripLocaleFromReturnTo(isClient() && (overrideReturnTo ? `${window.location.origin}${overrideReturnTo}` : window.location.href)) ?? '';
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());

    return `${baseUrl}/auth-gateway/login?onsuccess=${encodeURIComponent(returnTo)}${opco ? `&opco=${opco}` : ''}${iframe ? '&iframe=true' : ''}${silent ? '&prompt=none' : ''}${isLocalhost ? '&env=local' : ''}`;
  },

  createLogoutUrl: ({
    overrideReturnTo,
    localDevHostPatterns,
    gatewayUrl,
  }: { overrideReturnTo?: string; localDevHostPatterns?: string[]; gatewayUrl?: string } = {}) => {
    const returnTo =
      stripLocaleFromReturnTo(isClient() && (overrideReturnTo ? `${window.location.origin}${overrideReturnTo}` : window.location.href)) ?? '';
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());

    const opco = Cookies.get('AVIOS_OPCO')?.toLowerCase();
    const sessionParamName = `${opco}_session_id`;
    const deleteParamName = `delete_${sessionParamName}`;

    const sessionId = Cookies.get(sessionParamName);

    return `${baseUrl}/auth-gateway/logout?returnto=${encodeURIComponent(`${returnTo}${isLocalhost ? `?${deleteParamName}=true` : ''}`)}${isLocalhost && sessionId ? `&${sessionParamName}=${sessionId}` : ''}${isLocalhost ? '&env=local' : ''}`;
  },

  exchangeSilentCode: async ({
    code,
    opco,
    localDevHostPatterns,
    gatewayUrl,
  }: {
    code: string;
    opco?: string;
    localDevHostPatterns?: string[];
    gatewayUrl?: string;
  }) => {
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? getCurrentOrigin();
    const url = `${baseUrl}/auth-gateway/token-exchange${isLocalhost ? '?env=local' : ''}`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ code, ...(opco ? { opco } : {}) }),
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },

  clearAuthSessionCookies: async ({ localDevHostPatterns, gatewayUrl }: { localDevHostPatterns?: string[]; gatewayUrl?: string } = {}) => {
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());
    const url = `${baseUrl}/auth-gateway/clear-session${isLocalhost ? '?env=local' : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },

  getUser: async ({ localDevHostPatterns, gatewayUrl }: { localDevHostPatterns?: string[]; gatewayUrl?: string } = {}) => {
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());
    const url = `${baseUrl}/auth-gateway/user`;
    const sessionId = Cookies.get(`${Cookies.get('AVIOS_OPCO')}_session_id`.toLowerCase());

    return fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(isLocalhost && sessionId ? { env: 'local', session_id: sessionId } : {}),
      },
      cache: 'no-store',
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },

  getBalance: async ({ localDevHostPatterns, gatewayUrl }: { localDevHostPatterns?: string[]; gatewayUrl?: string } = {}) => {
    const isLocalhost = isClient() && isLocalDevHost(window.location.hostname, localDevHostPatterns);
    const baseUrl = gatewayUrl ?? (isLocalhost ? DEV_URL : getCurrentOrigin());
    const url = `${baseUrl}/shell/api/users/current/accounts`;

    const opco = Cookies.get('AVIOS_OPCO')?.toLowerCase();
    const sessionCookieName = `${opco}_session_id`;

    const sessionId = Cookies.get(sessionCookieName);

    return fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(isLocalhost && sessionId ? { env: 'local', [sessionCookieName]: sessionId, avios_opco: Cookies.get('AVIOS_OPCO') } : {}),
      },
      ...(!isLocalhost ? { credentials: 'include' } : {}),
    });
  },
};
